import { computed, reactive } from "vue"
import axios from "axios"

import useNotify from "@Composables/useNotify.js"

import PlanModel, { type Plan, type PlanData, type PlanPrice } from "../Data/Models/PlanModel"
import type { SubscriptionData } from "../Data/Models/SubscriptionModel"

export type BillingPeriod = "monthly" | "yearly"

/**
 * Raw API price structure from backend
 */
interface ApiPlanPrice {
  interval: "monthly" | "yearly"
  price: {
    amount: number
    currency: string
  }
  features: string[]
  options: Record<string, number | string | null>
  active?: boolean
  hidden?: boolean
  trial?: boolean
}

/**
 * Raw API plan structure from backend
 */
interface ApiPlanData {
  name: string
  group?: string | null
  type: "user" | "team" | "organization"
  enterprise: boolean
  prices: ApiPlanPrice[]
  highlighted: string[]
  tags: string[]
  hidden?: boolean
  active?: boolean
}

/**
 * Transforms API plan data to the format expected by PlanModel
 */
function transformApiPlan(apiPlan: ApiPlanData): PlanData {
  // Get features and options from the first price (they're the same across intervals)
  const firstPrice = apiPlan.prices[0]

  // Transform prices to flat structure
  const prices: PlanPrice[] = apiPlan.prices
    .filter((p) => !p.trial) // Exclude trial prices
    .map((p) => ({
      id: `${apiPlan.name}_${p.interval}`,
      interval: p.interval,
      amount: p.price.amount,
      currency: p.price.currency,
    }))
    // Dedupe by interval (API may return duplicates for trial/non-trial)
    .filter((price, index, self) => self.findIndex((p) => p.interval === price.interval) === index)

  return {
    id: apiPlan.name,
    name: apiPlan.name,
    group: apiPlan.group ?? null,
    type: apiPlan.type,
    description: null,
    features: (firstPrice?.features ?? []) as PlanData["features"],
    highlighted: apiPlan.highlighted ?? [],
    options: (firstPrice?.options ?? {}) as PlanData["options"],
    prices,
    enterprise: apiPlan.enterprise,
    tags: apiPlan.tags ?? [],
  }
}

/**
 * Response from the subscription status endpoint
 */
export interface SubscriptionStatusResponse {
  current: SubscriptionData | null
  future: SubscriptionData | null
}

/**
 * Checkout session response from Stripe/Paddle
 */
export interface CheckoutSessionResponse {
  session_id?: string // Stripe
  transaction_id?: string // Paddle
  url?: string
}

/**
 * Options for initiating checkout
 */
export interface CheckoutOptions {
  plan: string
  type: "user" | "team" | "organization"
  interval: "monthly" | "yearly"
  currency: string
  coupon?: string | null
}

interface SubscriptionsApiState {
  plans: Plan[]
  loading: boolean
  error: string | null
}

export interface SubscriptionsApi {
  plans: ReturnType<typeof computed<Plan[]>>
  loading: ReturnType<typeof computed<boolean>>
  error: ReturnType<typeof computed<string | null>>
  fetchPlans: (orgSlug: string | null) => Promise<boolean>
  fetchSubscriptionStatus: (orgSlug: string | null) => Promise<SubscriptionStatusResponse>
  initCheckout: (orgSlug: string | null, options: CheckoutOptions) => Promise<CheckoutSessionResponse | null>
  updateSubscription: (orgSlug: string | null, options: CheckoutOptions) => Promise<boolean>
  cancelSubscription: (orgSlug: string | null) => Promise<boolean>
  resumeSubscription: (orgSlug: string | null) => Promise<boolean>
  cancelScheduledDowngrade: (orgSlug: string | null) => Promise<boolean>
  plansForCurrentPeriod: ReturnType<typeof computed<(billingPeriod?: BillingPeriod) => Plan[]>>
  clearError: () => void
  reset: () => void
  setRouter: (router: HttpInterface) => SubscriptionsApi
}

/**
 * Builds the API URL based on whether we're in user or org context
 */
function buildUrl(orgSlug: string | null, path: string): string {
  if (orgSlug) {
    return `/app/api/organization/${orgSlug}/${path}`
  }
  return `/app/api/settings/${path}`
}

/**
 * Creates a SubscriptionsApi instance for fetching plans and managing subscriptions.
 *
 * The API is context-agnostic - pass orgSlug to each method:
 * - null = user subscription endpoints
 * - string = organization subscription endpoints
 */
export default function SubscriptionsApi(): SubscriptionsApi {
  const { notifyError } = useNotify()

  const state = reactive<SubscriptionsApiState>({
    plans: [],
    loading: false,
    error: null,
  })

  let router: HttpInterface = axios

  return {
    plans: computed(() => state.plans),
    loading: computed(() => state.loading),
    error: computed(() => state.error),

    /**
     * Fetches available subscription plans
     */
    async fetchPlans(orgSlug: string | null): Promise<boolean> {
      state.loading = true
      state.error = null

      try {
        const url = orgSlug
          ? `/app/api/organization/${orgSlug}/subscriptions`
          : "/app/api/subscriptions"

        const response: HttpResponse<{ data: ApiPlanData[] } | ApiPlanData[]> = await router.get(url)

        // Handle API response structure: { data: [...] } or direct array
        const apiPlans: ApiPlanData[] = Array.isArray(response.data)
          ? response.data
          : response.data?.data ?? []

        // Transform and create Plan models
        state.plans = apiPlans
          .filter((plan) => plan.active !== false)
          .map((apiPlan) => PlanModel(transformApiPlan(apiPlan)))

        return true
      } catch (error: unknown) {
        state.error = error instanceof Error ? error.message : "Failed to fetch plans"
        notifyError({ text: state.error })
        return false
      } finally {
        state.loading = false
      }
    },

    /**
     * Fetches current and future subscription status
     */
    async fetchSubscriptionStatus(orgSlug: string | null): Promise<SubscriptionStatusResponse> {
      const url = orgSlug
        ? `/app/api/organization/${orgSlug}/subscriptions/status`
        : "/app/api/subscriptions/status"

      const response: HttpResponse<SubscriptionStatusResponse> = await router.get(url)
      return response.data
    },

    /**
     * Initiates checkout for a subscription plan
     * Returns checkout session data for Stripe or Paddle
     */
    async initCheckout(
      orgSlug: string | null,
      options: CheckoutOptions,
    ): Promise<CheckoutSessionResponse | null> {
      state.loading = true
      state.error = null

      try {
        const url = buildUrl(orgSlug, "subscription")

        // Build payload matching backend InitSubscriptionRequest expectations
        const payload: Record<string, string | undefined> = {
          subscription: options.plan,
          interval: options.interval,
          currency: options.currency,
          type: options.type,
        }

        // Only include coupon if provided
        if (options.coupon) {
          payload.coupon = options.coupon
        }

        const response: HttpResponse<CheckoutSessionResponse> = await router.post(url, payload)

        return response.data
      } catch (error: unknown) {
        state.error = error instanceof Error ? error.message : "Failed to initiate checkout"
        notifyError({ text: state.error })
        return null
      } finally {
        state.loading = false
      }
    },

    /**
     * Updates/swaps an existing subscription to a different plan
     * Use this when the user already has a subscription (including free)
     */
    async updateSubscription(orgSlug: string | null, options: CheckoutOptions): Promise<boolean> {
      state.loading = true
      state.error = null

      try {
        const url = buildUrl(orgSlug, "subscription")

        const payload: Record<string, string | undefined> = {
          subscription: options.plan,
          interval: options.interval,
          currency: options.currency,
          type: options.type,
        }

        if (options.coupon) {
          payload.coupon = options.coupon
        }

        await router.put(url, payload)
        return true
      } catch (error: unknown) {
        state.error = error instanceof Error ? error.message : "Failed to update subscription"
        notifyError({ text: state.error })
        return false
      } finally {
        state.loading = false
      }
    },

    /**
     * Cancels the current subscription
     */
    async cancelSubscription(orgSlug: string | null): Promise<boolean> {
      state.loading = true
      state.error = null

      try {
        const url = buildUrl(orgSlug, "subscription")
        await router.delete(url)
        return true
      } catch (error: unknown) {
        state.error = error instanceof Error ? error.message : "Failed to cancel subscription"
        notifyError({ text: state.error })
        return false
      } finally {
        state.loading = false
      }
    },

    /**
     * Resumes a cancelled subscription
     */
    async resumeSubscription(orgSlug: string | null): Promise<boolean> {
      state.loading = true
      state.error = null

      try {
        const url = buildUrl(orgSlug, "subscription")
        await router.patch(url)
        return true
      } catch (error: unknown) {
        state.error = error instanceof Error ? error.message : "Failed to resume subscription"
        notifyError({ text: state.error })
        return false
      } finally {
        state.loading = false
      }
    },

    /**
     * Cancels a scheduled downgrade
     */
    async cancelScheduledDowngrade(orgSlug: string | null): Promise<boolean> {
      state.loading = true
      state.error = null

      try {
        const url = buildUrl(orgSlug, "subscription/scheduled")
        await router.delete(url)
        return true
      } catch (error: unknown) {
        state.error = error instanceof Error ? error.message : "Failed to cancel scheduled downgrade"
        notifyError({ text: state.error })
        return false
      } finally {
        state.loading = false
      }
    },

    /**
     * Returns plans filtered by billing period (have a price for that interval)
     * Free and enterprise plans are always included regardless of billing period.
     */
    plansForCurrentPeriod: computed(() => (billingPeriod: BillingPeriod = "monthly"): Plan[] => {
      return state.plans.filter((plan) => {
        // Free and enterprise plans are always shown
        if (plan.name === "free" || plan.enterprise) {
          return true;
        }
        // Other plans must have a price for the current interval
        return plan.getPrice(billingPeriod) !== null;
      });
    }),

    /**
     * Clears the error state
     */
    clearError(): void {
      state.error = null
    },

    /**
     * Resets the API state to initial values
     */
    reset(): void {
      state.plans = []
      state.loading = false
      state.error = null
    },

    /**
     * Allows you to swap out the router for a fake one when writing tests
     */
    setRouter(newRouter) {
      router = newRouter
      return this
    },
  }
}
